/**
 * Central portal notifications — one model for the floating bell and the
 * Notifications page.
 *
 * Live sources (queries): task assignments/collaborations, due task reminders,
 * note reminders, handovers assigned to me, pending access requests (super
 * admins) and unresolved portal errors (super admins).
 *
 * Event sources (snapshot diff, persisted per user in localStorage): a new
 * HubSpot ticket assigned to me (owner/paralegal), a ticket changing stage,
 * and a task of mine moving not_started → in_progress. The first run only
 * seeds the snapshot (no flood of "new" events for everything you already had).
 */
import { personName } from '@/lib/people';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { caseName } from '@/lib/subjects';
import { useQuery } from '@tanstack/react-query';
import { useUser } from '@/auth/AuthContext';
import { WORKERS } from '@/data/workers';
import type { CountryCode } from '@/integrations/types';
import type { Ticket } from '@/integrations/types';
import { useWorkTasks, usePendingAccounts } from '@/hooks/useWorkspaceData';
import { useHubspotBoard } from '@/hooks/useHubspotBoard';
import { fetchParallelPending } from '@/integrations/workspaceApi';
import { resolveParallel } from '@shared/parallelRef';
import { fetchMyNotifications } from '@/integrations/portalConfig';
import { useSeen } from '@/hooks/useSeen';
import type { MyWorkTab } from '@/hooks/useMyWorkTab';

const nameOf = (id: string | null) => personName(id);

/* Session-to-session snapshots of the slow feeds: on reload the LAST known
 * result paints instantly (no 0 → N counter jump) while the real fetch
 * refreshes silently behind it. */
function snapGet<T>(key: string): T | undefined {
  try { const s = localStorage.getItem(key); return s ? (JSON.parse(s) as T) : undefined; } catch { return undefined; }
}
function snapSet(key: string, v: unknown) {
  try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* ignore */ }
}

export type NotifKind =
  | 'assigned' | 'collab' | 'reminder' | 'note' | 'handover' | 'ho-reminder'
  | 'ho-mention' | 'ho-alert' | 'ho-team' | 'waiting-on-you' | 'planner-digest'
  | 'note-tag' | 'note-update'
  | 'ticket-new' | 'ticket-stage' | 'task-started' | 'task-status'
  | 'docs-pending' | 'docs-reminder' | 'case-date'
  | 'link-approved' | 'link-rejected' | 'link-request'
  | 'access' | 'app-error' | 'feedback' | 'feedback-reply'
  | 'resource-request' | 'resource-approved' | 'resource-rejected'
  | 'task-file' | 'task-comment' | 'ticket-back'
  | 'case-renewal-due' | 'case-appeal-due' | 'case-expiry-due' | 'case-reminder-set'
  /* Lo que Actes copia a esta bandeja: `actes-<type>` del trigger. */
  | 'actes-assignment' | 'actes-assigned' | 'actes-dpc_due' | 'actes-expiry' | 'actes-intake' | 'actes-demande'
  | 'actes-acte_change' | 'actes-acte_no_dossier' | 'actes-digest';

/**
 * What "important" means — decided HERE, in one place, for every source.
 *
 * The badge counts these and the bell lists these, so the number always equals
 * what you find when you open it. Sources used to each declare their own
 * importance (the database row, the snapshot event, the query), which is how
 * system errors ended up lighting the bell.
 *
 * Important = something happened that you have to go and look at:
 *   · your case moved — assigned to you, or its stage changed
 *   · the client uploaded documents, or they are still unreviewed after 7 days
 *   · a note landed on a case you own or collaborate on, or you were tagged
 *   · a task was assigned to you, or a task of yours changed status
 *   · a date on one of your cases — put there, or coming up
 *   · a reminder you set, or a handover put in your hands
 *   · a decision on a link you suggested — approved or rejected
 *   · super admins: someone asked for a new Quick Access link, or for portal
 *     access
 *
 * Others — portal and system errors, and edits to a note you were tagged in.
 * Recorded and readable under "Others" on the Notifications page; they just
 * don't chase anyone.
 */
/* ── LA CAMPANA NO LA LEÍA NADIE ──
   Medido sobre treinta días: 665 avisos enviados, vistos entre el 0 % y el
   20 % según el tipo — «waiting on you» 0 de 10, «documento adjunto» 0 de 15,
   «nota» 4 de 81. La lista de abajo marcaba como importante casi todo:
   cada cambio de etapa, cada nota, cada cambio de estado de una tarea. La
   gente aprendió a ignorar la campana, y lo urgente murió con lo demás.
   La regla ahora es una pregunta: ¿ME PIDE ALGO, o sólo me informa? Lo que
   pide suena y cuenta; lo que informa está en «Ver todas», en el resumen del
   día, y en una línea discreta al pie de la campana. */
const IMPORTANT_KINDS = new Set<string>([
  /* Un caso nuevo mío; documentos que me esperan. */
  'ticket-new', 'docs-pending', 'docs-reminder',
  /* Alguien me ha nombrado o me ha dado trabajo. */
  'note-tag', 'assigned', 'collab', 'waiting-on-you',
  /* Una fecha que me toca. */
  'reminder', 'case-date', 'ho-reminder', 'ho-mention', 'ho-alert',
  /* Un recordatorio que alguien puso a propósito y hoy vence: renovación, recurso, caducidad. */
  'case-renewal-due', 'case-appeal-due', 'case-expiry-due',
  /* Alguien espera mi respuesta (administración). El feedback también: un
     compañero pide algo y espera que se lea (Gerónimo, 23 sep: «no me llegan
     las notificaciones de feedback» — estaba en la lista de lo que sólo
     informa, así que ni sonaba, ni iba al correo, ni al móvil). */
  'access', 'link-request', 'resource-request', 'app-error', 'feedback',
  /* Me han contestado. */
  'feedback-reply', 'link-approved', 'link-rejected', 'resource-approved', 'resource-rejected',
  /* Actes: me lo han asignado, o un plazo mío. */
  'actes-assignment', 'actes-assigned', 'actes-dpc_due', 'actes-expiry', 'actes-intake', 'actes-demande',
  /* Alguien me ha escrito en una tarea (29 sep 2026). */
  'task-comment',
  /* A case put away until a date is back (5 Oct 2026). */
  'ticket-back',
]);
/* Y lo que sólo informa, dicho para que nadie lo vuelva a meter arriba sin
   pensarlo: 'ticket-stage', 'note', 'note-update', 'task-status',
   'task-started', 'task-file', 'handover', 'ho-team',
   'planner-digest', 'actes-acte_change', 'actes-acte_no_dossier', 'actes-digest'. */
export const isImportantKind = (kind: string): boolean => IMPORTANT_KINDS.has(kind);

export interface PortalNotif {
  id: string;
  kind: NotifKind;
  /** Personal & actionable (new ticket/task/handover/note) vs informational. */
  important: boolean;
  /** My role on the underlying ticket, when the notification has one. */
  role?: 'owner' | 'paralegal' | 'lawyer' | null;
  /** ISO timestamp when known — drives the date filter and day grouping. */
  date: string | null;
  text: string;
  by?: string;
  /** Navigation target ('parallel' opens `url` in a new tab). */
  target: 'mywork' | 'admin' | 'parallel' | 'tools' | 'planner';
  tab?: MyWorkTab;
  /** How many notifications this one stands for, when a burst was collapsed. */
  groupCount?: number;
  /** Ticket id (open its detail), task id, or handover id — per kind. */
  refId?: string;
  url?: string;
}

/** Concise identity from a HubSpot ticket subject like
 *  "Andrea Mogensen - 2 visitor visa renewal (10% discount) - France - (Visitor Visa)"
 *  → client "Andrea Mogensen", service "Visitor Visa". */
export function parseTicketSubject(subject: string, pipelineLabel?: string): { client: string; service: string } {
  /* ONE parser. This used to be a third copy of the subject heuristic and it
     drifted: "SP - Non Lucrative…" still named the client "SP" here after
     src/lib/subjects.ts had fixed it. Every surface reads the same function. */
  return caseName({ subject, pipelineLabel });
}

/* ---------------- persisted snapshot-diff events ---------------- */

interface StoredEvent {
  id: string;
  kind: 'ticket-new' | 'ticket-stage' | 'task-started';
  important: boolean;
  role?: 'owner' | 'paralegal' | 'lawyer' | null;
  date: string;
  text: string;
  by?: string;
  /** Ticket/task id — lets "View" open the exact thing. */
  refId?: string;
}

const evListeners = new Set<() => void>();
const evCache = new Map<string, StoredEvent[]>();

function evKey(meId: string) { return `lexidy-ntf-ev-${meId}`; }

function readEvents(meId: string): StoredEvent[] {
  const cached = evCache.get(meId);
  if (cached) return cached;
  let list: StoredEvent[] = [];
  try { list = JSON.parse(localStorage.getItem(evKey(meId)) || '[]') as StoredEvent[]; } catch { /* ignore */ }
  evCache.set(meId, list);
  return list;
}

function pushEvents(meId: string, events: StoredEvent[]) {
  if (!events.length) return;
  const cur = readEvents(meId);
  const have = new Set(cur.map((e) => e.id));
  const fresh = events.filter((e) => !have.has(e.id));
  if (!fresh.length) return;
  const next = [...fresh, ...cur].slice(0, 120);
  evCache.set(meId, next);
  try { localStorage.setItem(evKey(meId), JSON.stringify(next)); } catch { /* ignore */ }
  evListeners.forEach((l) => l());
}

/** useSyncExternalStore compares snapshots BY IDENTITY, so getSnapshot has to
 *  return the same object until the data really changes. readEvents() caches
 *  per person, but the no-person branch used to build a fresh [] on every call
 *  — which meant React saw a new snapshot on every render and looped until it
 *  gave up (#185). That only ever hit people with no roster entry, since
 *  everyone else took the cached path. One shared empty array fixes it. */
const NO_EVENTS: StoredEvent[] = [];
function useStoredEvents(meId: string | null): StoredEvent[] {
  return useSyncExternalStore(
    (cb) => { evListeners.add(cb); return () => evListeners.delete(cb); },
    () => (meId ? readEvents(meId) : NO_EVENTS),
    () => (meId ? readEvents(meId) : NO_EVENTS),
  );
}

/** Diff the ticket board against the stored snapshot → new-ticket / stage events. */
function diffTickets(meId: string, meIds: string[], tickets: Ticket[]) {
  const key = `lexidy-ntf-tk-${meId}`;
  let snap: Record<string, string> | null = null;
  try { snap = JSON.parse(localStorage.getItem(key) || 'null'); } catch { /* ignore */ }
  const now = new Date().toISOString();
  const mine = tickets.filter((t) => meIds.includes(t.ownerId) || (t.paralegalId && meIds.includes(t.paralegalId))
    || (t.paralegal2Id && meIds.includes(t.paralegal2Id))
    || (t.lawyerId && meIds.includes(t.lawyerId)));
  const next: Record<string, string> = {};
  const events: StoredEvent[] = [];
  for (const t of mine) {
    const role: 'owner' | 'paralegal' | 'lawyer' = meIds.includes(t.ownerId) ? 'owner'
      : (t.paralegalId && meIds.includes(t.paralegalId)) || (t.paralegal2Id && meIds.includes(t.paralegal2Id)) ? 'paralegal' : 'lawyer';
    next[t.id] = t.stageLabel;
    if (snap === null) continue; // first run: seed only
    const prev = snap[t.id];
    // Concise identity: client name as the title, service in the sub-line.
    const { client, service } = parseTicketSubject(t.subject, t.pipelineLabel);
    if (prev === undefined) {
      events.push({ id: `tknew:${t.id}`, kind: 'ticket-new', important: true, role, date: now, text: client, by: service, refId: t.id });
    } else if (prev !== t.stageLabel) {
      events.push({
        id: `tkstg:${t.id}:${t.stageLabel}`, kind: 'ticket-stage', important: false, role,
        date: now, text: client, by: `${service} · ${prev} → ${t.stageLabel}`, refId: t.id,
      });
    }
  }
  try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* ignore */ }
  pushEvents(meId, events);
}

/* ---------------- the aggregating hook ---------------- */

/**
 * Nine to twenty tasks landing on one case, in one line.
 *
 * Seeding a new file creates every step its service asks for — nine for a
 * visitor visa, twenty for a renewal — and each one was a notification of its
 * own. Twenty lines saying the same thing is how a feed becomes wallpaper: you
 * scroll past it, and past the one line that mattered underneath.
 *
 * Same case (refId), same kind, same hour → one entry that says how many and
 * opens the case. Two tasks is not a burst, so two stay as two: collapsing a
 * pair hides a name to save a line, which is a bad trade.
 *
 * Read state follows the group id, which is derived from the case and the hour,
 * so it is stable between refreshes — marking the group read does not have the
 * whole burst reappear tomorrow under a new id.
 */
function collapseTaskBursts(list: PortalNotif[]): PortalNotif[] {
  const BURST = 3;
  const hourOf = (iso: string | null) => (iso ? iso.slice(0, 13) : '');
  const groups = new Map<string, PortalNotif[]>();
  const out: PortalNotif[] = [];
  for (const n of list) {
    const groupable = (n.kind === 'assigned' || n.kind === 'collab') && !!n.refId;
    if (!groupable) { out.push(n); continue; }
    const key = `${n.kind}|${n.refId}|${hourOf(n.date)}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(n);
    else { groups.set(key, [n]); out.push(n); }   // placeholder keeps the order
  }
  return out.map((n) => {
    const groupable = (n.kind === 'assigned' || n.kind === 'collab') && !!n.refId;
    if (!groupable) return n;
    const key = `${n.kind}|${n.refId}|${hourOf(n.date)}`;
    const bucket = groups.get(key) || [n];
    if (bucket.length < BURST) return n;
    return {
      ...n,
      id: `grp:${key}`,
      // The count IS the message; the case is what you open.
      text: `${bucket.length} · ${n.text}`,
      groupCount: bucket.length,
      tab: 'tasks' as MyWorkTab,
    };
  });
}

export function useNotifications(isSuper: boolean) {
  const { user } = useUser();
  const me = WORKERS.find((w) => w.email?.toLowerCase() === (user?.email || '').toLowerCase());
  const meId = me?.id || null;
  const country = (me?.country as CountryCode) || null;

  const tasks = useWorkTasks(country, meId);
  // Phase 2: the SERVER writes one notification per recipient (tasks, notes,
  // tags, handovers, link decisions…) — identical on every device, pushed live.
  const feedSnapKey = `lexidy-ntf-feed-${(user?.email || '').toLowerCase()}`;
  const [feedSnap] = useState(() => snapGet<Awaited<ReturnType<typeof fetchMyNotifications>>>(feedSnapKey));
  const serverFeed = useQuery({
    queryKey: ['my-notifications'] as const,
    queryFn: async ({ signal }) => {
      const rows = await fetchMyNotifications(signal).catch(() => []);
      snapSet(feedSnapKey, rows.slice(0, 120));
      return rows;
    },
    enabled: !!user,
    /* The live channel is what makes this instant; this is the floor for when
       it is not there. A minute was long enough to feel broken. */
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
    placeholderData: feedSnap,
  });
  const pending = usePendingAccounts(isSuper);
  const [ppSnap] = useState(() => snapGet<Awaited<ReturnType<typeof fetchParallelPending>>>(`lexidy-ntf-ppsnap-${meId}`));
  const board = useHubspotBoard(country || '', me?.hubspotOwnerId || me?.email || '', 'both', !!country && !!me);
  /* The two-minute poll for portal errors is gone with the notifications it
     fed. Nothing read it any more, and every super admin was paying for it on
     a loop. The Errors panel fetches its own list when you open it. */
  // Documents the client uploaded in Parallel, still waiting for review.
  const parallelPending = useQuery({
    queryKey: ['parallel-pending', country, meId] as const,
    queryFn: async ({ signal }) => {
      const r = await fetchParallelPending(country as CountryCode, me?.hubspotOwnerId || me?.email || '', signal).catch(() => ({ items: [] }));
      snapSet(`lexidy-ntf-ppsnap-${meId}`, r);
      return r;
    },
    enabled: !!country && !!me,
    refetchInterval: 10 * 60_000,
    staleTime: 8 * 60_000,
    placeholderData: ppSnap,
  });

  // Remember when each ticket's pending docs were FIRST seen — that timestamp
  // dates the notification and drives the 7-day "don't forget" reminder.
  const pendingItems = parallelPending.data?.items;
  useEffect(() => {
    if (!meId || !pendingItems) return;
    const key = `lexidy-ntf-pd-${meId}`;
    let map: Record<string, string> = {};
    try { map = JSON.parse(localStorage.getItem(key) || '{}') as Record<string, string>; } catch { /* ignore */ }
    const now = new Date().toISOString();
    const next: Record<string, string> = {};
    for (const it of pendingItems) next[it.ticketId] = map[it.ticketId] || now;
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* ignore */ }
  }, [meId, pendingItems]);

  // Snapshot diffing (persists events so they survive reloads).
  const boardItems = board.data?.items;
  useEffect(() => {
    if (!meId || !boardItems) return;
    const meIds = [me?.hubspotOwnerId, me?.email, meId].filter(Boolean) as string[];
    diffTickets(meId, meIds, boardItems);
  }, [meId, boardItems]); // eslint-disable-line react-hooks/exhaustive-deps
  const taskItems = tasks.data?.items;

  const events = useStoredEvents(meId);
  const seen = useSeen();

  const all = useMemo<PortalNotif[]>(() => {
    const out: PortalNotif[] = [];
    // Access requests (super admin) — informational.
    for (const a of pending.data || []) {
      out.push({
        id: `access:${a.email}:${a.requestedAt || ''}`, kind: 'access', important: false,
        date: a.requestedAt || null, text: a.email, target: 'admin',
      });
    }
    /* SYSTEM ERRORS DO NOT NOTIFY. Anybody, ever.
       A notification is a request for attention from a person, and a portal
       error is not that: it is something to look at when you sit down to look
       at errors. Mixed into the same feed as "this case was assigned to you",
       it trains everyone to ignore the bell — and the useful ones with it.
       They are still all there, unresolved and counted, in Administration →
       Portal errors, which is the screen built to read them. */
    // The server-generated feed (tasks, notes, tags, handovers, link events).
    for (const r of serverFeed.data || []) {
      const kind = r.kind as NotifKind;
      let by = r.sub || undefined;
      if (kind === 'assigned' || kind === 'collab' || kind === 'waiting-on-you' || kind === 'ho-mention' || kind === 'ho-alert') by = nameOf(r.sub || null);
      if (kind === 'note' || kind === 'note-tag' || kind === 'note-update') {
        by = r.sub ? parseTicketSubject(r.sub).client : undefined;
      }
      out.push({
        id: `db:${r.id}`, kind, important: r.important === true,
        role: (r.role as PortalNotif['role']) || null,
        date: r.created_at || null, text: r.title, by,
        target: (r.target as PortalNotif['target']) || 'mywork',
        tab: (r.tab as MyWorkTab) || undefined,
        refId: r.ref_id || undefined, url: r.url || undefined,
      });
    }
    if (meId) {
      /* ── LE RAPPEL N'EST PLUS DÉDUIT ICI ──
         Il l'était : « remind_at est passé, donc affiche une ligne ». D'où les
         deux plaintes — il n'atteignait que celui qui avait la tâche dans SA
         liste (impossible de le faire partir à toute l'équipe, les autres ne
         l'avaient pas), et il ne restait nulle part : lu, ou la tâche
         terminée, il s'évaporait sans rien laisser dans l'historique.
         Il est maintenant ÉCRIT par le serveur (cron `task-reminders`), donc
         il arrive à qui on a dit et il reste. Le déduire encore ici
         l'afficherait deux fois. */
      // Snapshot events (new ticket / stage change / task started).
      for (const e of events) {
        out.push({
          id: e.id, kind: e.kind, important: e.important, role: e.role || null,
          date: e.date, text: e.text, by: e.by, refId: e.refId,
          target: 'mywork', tab: e.kind === 'task-started' ? 'tasks' : 'tickets',
        });
      }
      // Parallel documents pending review (+ a weekly reminder past 7 days).
      let firstSeen: Record<string, string> = {};
      try { firstSeen = JSON.parse(localStorage.getItem(`lexidy-ntf-pd-${meId}`) || '{}') as Record<string, string>; } catch { /* ignore */ }
      for (const it of pendingItems || []) {
        const { client, service } = parseTicketSubject(it.subject, it.pipelineLabel);
        const since = firstSeen[it.ticketId] || null;
        const url = resolveParallel(it.parallelId, it.parallelUrl).parallelUrl || undefined;
        out.push({
          id: `pdoc:${it.ticketId}:${it.pending}`, kind: 'docs-pending', important: true,
          date: since, text: client, by: `${service} · ${it.pending} doc${it.pending === 1 ? '' : 's'}`,
          target: 'parallel', url, refId: it.ticketId,
        });
        if (since) {
          const days = Math.floor((Date.now() - new Date(since).getTime()) / 86_400_000);
          if (days >= 7) {
            const week = Math.floor(days / 7); // re-fires each week it stays pending
            out.push({
              id: `pdocrem:${it.ticketId}:${week}`, kind: 'docs-reminder', important: true,
              date: new Date().toISOString(), text: client,
              by: `${service} · ${it.pending} doc${it.pending === 1 ? '' : 's'} · ${days}d`,
              target: 'parallel', url, refId: it.ticketId,
            });
          }
        }
      }
    }
    // Importance is decided here and nowhere else, so old rows written with the
    // wrong flag (system errors marked important, stage moves marked not) are
    // reclassified on read instead of staying wrong forever.
    for (const n of out) n.important = isImportantKind(n.kind);
    // Newest first; undated last.
    out.sort((a, b) => (b.date ? new Date(b.date).getTime() : 0) - (a.date ? new Date(a.date).getTime() : 0));
    return collapseTaskBursts(out);
  }, [pending.data, serverFeed.data, taskItems, events, pendingItems, meId]);

  const unseen = useMemo(() => all.filter((n) => !seen.has(n.id)), [all, seen]);

  /** Only the important ones — what the bell shows AND what it counts, so the
   *  number and the list can never disagree. */
  const important = useMemo(() => all.filter((n) => n.important), [all]);
  const unseenImportant = useMemo(() => important.filter((n) => !seen.has(n.id)), [important, seen]);
  const count = unseenImportant.length;

  return { all, unseen, important, unseenImportant, count, seen, meId };
}
